/**
 * The one place a `Response` is turned into a value or an error.
 *
 * Everything the API can answer is one of three things — a resource, an
 * `{ error: { code, message } }` envelope, or nothing at all because the
 * request never left the device. Views should never have to tell those apart,
 * so no `Response` escapes this module.
 */

export interface FieldError {
  path: string;
  message: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fields: FieldError[];

  constructor(status: number, code: string, message: string, fields: FieldError[] = []) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.fields = fields;
  }

  /** No HTTP response at all — the request never reached the server. */
  get offline(): boolean {
    return this.status === 0;
  }
}

const OFFLINE_MESSAGE = "Can't reach Trayflux. Check your connection and try again.";

interface ErrorEnvelope {
  code?: unknown;
  message?: unknown;
  fields?: unknown;
}

function envelope(body: unknown): ErrorEnvelope {
  if (typeof body !== "object" || body === null) return {};
  const error = (body as { error?: unknown }).error;
  if (typeof error !== "object" || error === null) return {};
  return error as ErrorEnvelope;
}

function fieldErrors(value: unknown): FieldError[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    if (typeof entry !== "object" || entry === null) return [];
    const { path, message } = entry as { path?: unknown; message?: unknown };
    if (typeof path !== "string" || typeof message !== "string") return [];
    return [{ path, message }];
  });
}

export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      ...init,
      headers: { "content-type": "application/json", ...(init.headers ?? {}) },
      // The session cookie is HttpOnly; this is what sends it.
      credentials: "same-origin",
    });
  } catch {
    // fetch rejects only for a transport failure — no network, DNS, TLS. Every
    // HTTP status resolves, 500 included, so this branch really does mean the
    // server was never reached.
    throw new ApiError(0, "offline", OFFLINE_MESSAGE);
  }

  // Tolerated rather than required: an error from Caddy rather than the API
  // (a 502 while the container restarts) is not JSON, and the status still has
  // to be reported honestly.
  const body: unknown = await res.json().catch(() => undefined);

  if (!res.ok) {
    const error = envelope(body);
    throw new ApiError(
      res.status,
      typeof error.code === "string" ? error.code : "unknown",
      typeof error.message === "string" ? error.message : "Something went wrong.",
      fieldErrors(error.fields),
    );
  }

  return body as T;
}

export function send<T>(method: string, path: string, payload?: unknown): Promise<T> {
  return request<T>(path, {
    method,
    body: payload === undefined ? undefined : JSON.stringify(payload),
  });
}
